import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { capture } from "@/lib/posthog";
import { ArrowUpRight, BookOpen, Compass, MessageCircle, Siren } from "lucide-react";
import { Icon } from "@/pages/prototype-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { useResolvedEntitlements } from "@/hooks/use-entitlements";
import type { SystemRole } from "@/lib/rbac";
import { guideForRoute } from "@/lib/page-guides";
import { OPEN_GUIDE_EVENT } from "@/components/PageGuideModal";
import { OPEN_LIVE_CHAT_EVENT } from "@/components/IntercomWidget";
import { UrgentEscalationDialog } from "@/components/UrgentEscalationDialog";
import { helpMenuTestIds as ids } from "@/testids/helpMenuTestIds";

/**
 * One place to go when you are stuck.
 *
 * The three answers used to be three unrelated things: a book icon for the
 * docs, a second book icon for the page guide, and the support chat. Two
 * identical glyphs side by side told you nothing about which was which.
 *
 * They are now one Help menu, ordered by how specific the answer is: this
 * page, then the manual, then a person. The chat bubble also shows on its own
 * by default (IntercomWidget); it can be closed there, and "Contact support"
 * here opens the chat and brings it back.
 *
 * One line each. Every row used to carry a second, smaller line -- "Workflows,
 * step by step", "Opens in a new tab", "Chat with our team" -- which restated
 * the label it sat under and set a 12px line against the row's own 13.12px, a
 * gap too small to read as hierarchy and large enough to read as a mistake.
 * Where the second line said something the label could not, it is now a glyph:
 * the arrow on Documentation is the standard mark for leaving the app, and it
 * keeps its words for screen readers.
 */
export function HelpMenu({ docsUrl }: { docsUrl: string }): React.ReactElement {
  const location = useLocation();
  const { userContext, impersonating } = useAuth();
  const role = userContext?.role as SystemRole | undefined;

  // Only where the page actually has one, so the item is never a dead entry.
  // That means BOTH conditions: a guide for this route, and the guide feature
  // not switched off for the org. PageGuideModal unmounts on an explicit
  // false, so offering the tour then dispatches OPEN_GUIDE_EVENT at a listener
  // that is not there. Same semantics as the modal: hide only on an explicit
  // false, never while the lookup is loading or has errored.
  const entitlements = useResolvedEntitlements();
  const guidanceOff = entitlements.isSuccess && entitlements.data?.["guidance.pageGuide"] === false;
  const guide = guidanceOff ? null : guideForRoute(location.pathname, location.search, role);
  const where = { route: location.pathname, has_guide: !!guide };

  // Same gate as POST /api/v1/support/escalations: the org holds the
  // entitlement (explicit true; absent or loading hides it) and the caller is
  // an owner, admin or developer. platform_admin passes there too, so it passes here.
  // Hidden while impersonating: the api refuses a platform admin acting as the
  // customer, and it would page under the customer's name.
  const canEscalate =
    !impersonating &&
    entitlements.data?.["support.priorityEscalation"] === true &&
    (role === "owner" || role === "admin" || role === "developer" || role === "platform_admin");
  const [escalating, setEscalating] = useState(false);

  return (
    <>
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) capture("help_menu_opened", where);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="fx-btn fx-btn--ghost fx-btn--icon fx-btn--sm"
          aria-label="Help"
          title="Help"
          data-testid={ids.trigger}
        >
          {/* The repo's own glyph set, not lucide, because the icons either
              side of this one come from it: they are filled, and a 1.5-weight
              outline beside them reads as a lighter, different kind of
              control. */}
          <Icon name="question" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60" data-testid={ids.menu}>
        {guide && (
          <DropdownMenuItem
            onSelect={() => {
              capture("help_menu_item_selected", { item: "tour", ...where });
              window.dispatchEvent(new Event(OPEN_GUIDE_EVENT));
            }}
            data-testid={ids.guideItem}
          >
            <Compass className="size-4 shrink-0" aria-hidden />
            <span>Take a tour</span>
          </DropdownMenuItem>
        )}

        {/* A real anchor, not a menu button with an onClick, so middle-click
            and "open in new tab" work — which is how anyone actually reads docs
            while using the product. `noopener` because `target="_blank"`
            otherwise hands the docs site a live handle on this window.

            Absolute and external by nature, so deliberately NOT routed through
            appUrl(): it leaves the app, and the `/gcp/` base path must not be
            prefixed onto it. */}
        <DropdownMenuItem asChild>
        <a
          href={docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[0.82rem]",
            "cursor-pointer text-foreground no-underline hover:bg-accent/50",
          )}
          onClick={() => capture("help_menu_item_selected", { item: "docs", ...where })}
          data-testid={ids.docsItem}
        >
          <BookOpen className="size-4 shrink-0" aria-hidden />
          <span>Documentation</span>
          <ArrowUpRight className="ml-auto size-3.5 shrink-0 text-fg-muted" aria-hidden />
          {/* The arrow is the whole message for anyone who can see it. Kept as
              words too, in the row's own content rather than an aria-label,
              because an aria-label would replace "Documentation" instead of
              adding to it. */}
          <span className="sr-only">Opens in a new tab</span>
        </a>
        </DropdownMenuItem>

        <div role="separator" className="my-1 h-px bg-border" />

        <DropdownMenuItem
          onSelect={() => {
            capture("help_menu_item_selected", { item: "support", ...where });
            window.dispatchEvent(new Event(OPEN_LIVE_CHAT_EVENT));
          }}
          data-testid={ids.chatItem}
        >
          <MessageCircle className="size-4 shrink-0" aria-hidden />
          <span>Contact support</span>
        </DropdownMenuItem>

        {canEscalate && (
          <DropdownMenuItem
            onSelect={() => {
              capture("help_menu_item_selected", { item: "urgent_escalation", ...where });
              setEscalating(true);
            }}
            data-testid={ids.urgentItem}
          >
            <Siren className="size-4 shrink-0" aria-hidden />
            <span>Escalate to on-call support</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    {escalating && <UrgentEscalationDialog onClose={() => setEscalating(false)} />}
    </>
  );
}
